import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import type { UserListItem } from '~settings/api/lib/schemas';
import { useUserList } from '~settings/api/queries/userList';
import { FilterDrawer } from '~settings/components/FilterDrawer';
import { HeaderSmall } from '~settings/components/HeaderSmall';
import { ListControls } from '~settings/components/ListControls';
import { TableCellSkeleton } from '~settings/components/TableCellSkeleton';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { useDefaultSort } from '~settings/hooks/useDefaultSort';
import { useFilters } from '~settings/hooks/useFilters';
import { useSearch } from '~settings/hooks/useSearch';
import { UserFormFilter } from '~settings/modules/user/components/UserFormFilter';

import { useUsersColumns } from './hooks/useUsersColumns';

const userEmptyArray: UserListItem[] = [];

interface UsersProps {
  isSublabel: boolean;
  labelId?: string;
  total: number;
}

const DEFAULT_FILTER = { isActive: 'true' };

export function Users({ isSublabel, labelId, total }: UsersProps) {
  const { t } = useTranslation('settings');
  const columns = useUsersColumns(labelId);
  const navigate = useNavigate();

  const { searchTerm } = useSearch();
  const { page, setPageNumber, perPage, skip, take } = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();

  const { filterData, drawerOpen, closeDrawer, openDrawer } = useFilters({
    defaultFilter: DEFAULT_FILTER,
  });

  const { sorting, setSorting, orderBy } = useDefaultSort({
    defaultSorting: [{ id: 'fullName', desc: false }],
  });

  const { userList, isFetching } = useUserList({
    skip,
    take,
    labelIds: labelId,
    ...(orderBy && { orderBy }),
    ...filterData,
    ...(searchTerm && { search: searchTerm }),
  });

  const onClick = (row: UserListItem) => {
    navigate(SETTINGS_ROUTES.USER_DETAIL(row.id));
  };

  useEffect(() => {
    if (!searchParams.size) {
      setSearchParams(new URLSearchParams(DEFAULT_FILTER));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once page loads

  return (
    <>
      {isSublabel ? (
        <HeaderSmall>
          {t('labelDetails.usersCount', {
            usersCount: total,
          })}
        </HeaderSmall>
      ) : null}
      <ListControls
        placeholder={t('user.search')}
        page={page}
        total={userList?.total ?? 0}
        perPage={perPage}
        entity="users"
        onFilterClick={openDrawer}
        setPageNumber={setPageNumber}
      />
      <div className="pt-lg">
        <Table
          CellSkeleton={TableCellSkeleton}
          columns={columns}
          data={userList?.data ?? userEmptyArray}
          isLoading={isFetching}
          level={3}
          loadingRowCount={10}
          onRowClick={onClick}
          onSortingChange={setSorting}
          sort={sorting}
          size="md"
          variant="striped"
        />
      </div>
      <FilterDrawer open={drawerOpen} onOpenChange={closeDrawer}>
        <UserFormFilter
          open={drawerOpen}
          onApply={closeDrawer}
          options={{ showLabels: false, showLabelGroups: false }}
        />
      </FilterDrawer>
    </>
  );
}

Users.displayName = 'LabelUsers';
